import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import { v4 as uuidv4 } from 'uuid';

import { useSSOConfig, useSSOLogin } from '@/api/endpoints/auth';
import { buildSSOPopupName, usePopupWindow } from '@/hooks/use-popup-window';
import { extractHTTPError } from '@/utils/http-client';

type SSOOptionsProps = {
  onError: (error: string) => void;
};

// The popup self-closes shortly after broadcasting its result, but BroadcastChannel
// delivery is asynchronous. When we observe the popup has closed, wait this long for a
// result to arrive before treating the closure as a cancellation, so an in-flight
// 'sso-success' isn't mistaken for the user dismissing the window.
const POPUP_RESULT_GRACE_MS = 400;

export const useSSOOptions = ({ onError }: SSOOptionsProps) => {
  const router = useRouter();

  const { data: ssoConfig, error: ssoConfigError, isLoading: isLoadingSSOConfig } = useSSOConfig();
  const [initiateSSO, { isPending: isInitiatingSSO }] = useSSOLogin();

  const { openPopup } = usePopupWindow();
  const pollTimerRef = useRef<number | undefined>(undefined);
  const graceTimerRef = useRef<number | undefined>(undefined);
  const resolvedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    };
  }, []);

  const ssoOptions = useMemo(() => {
    if (isLoadingSSOConfig || !ssoConfig?.enabled || !ssoConfig.providers?.length) return;

    return ssoConfig.providers;
  }, [isLoadingSSOConfig, ssoConfig]);

  const handleSSOLogin = useCallback(
    async (providerId: string) => {
      const redirectUri = window.location.origin + router.buildLocation({ to: '/callback' }).href;

      const response = await initiateSSO({
        idp: providerId,
        redirect_uri: redirectUri,
      });

      if (response.error) {
        onError(extractHTTPError(response.error, 'An unexpected error occurred'));
        return;
      }

      resolvedRef.current = false;

      // Correlation token: encoded into the popup window name and required on the result
      // message, so only this login's popup can complete this flow (defends against a
      // foreign same-origin tab on the shared BroadcastChannel and concurrent logins).
      const token = uuidv4();

      const result_popup = openPopup(response.data.sso_url, buildSSOPopupName(token), {
        expectedToken: token,
        onMessage: (data) => {
          resolvedRef.current = true;
          // Stop both timers: the poll that watches for a closed popup, and any grace
          // timer it may already have scheduled (the resolvedRef guard makes a late grace
          // callback harmless, but clearing it here keeps the intent local).
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = undefined;
          }
          if (graceTimerRef.current) {
            clearTimeout(graceTimerRef.current);
            graceTimerRef.current = undefined;
          }
          if (data.type === 'sso-success') {
            onError('');
            router.navigate({ to: '/' });
          } else if (data.type === 'sso-error') {
            onError(data.error || 'An unexpected error occurred');
          }
        },
      }) as { popup: Window | null; cleanup: () => void } | null;

      if (!result_popup || !result_popup.popup) {
        onError('Please allow popups for this site to use SSO login');
        return;
      }

      const { popup, cleanup } = result_popup;
      pollTimerRef.current = setInterval(() => {
        if (!popup.closed) return;
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = undefined;
        // Give an in-flight result a grace period to arrive before declaring cancellation:
        // the popup closes ~immediately after broadcasting, but delivery is async.
        graceTimerRef.current = window.setTimeout(() => {
          cleanup();
          if (!resolvedRef.current) {
            onError('Authentication was cancelled or failed');
          }
        }, POPUP_RESULT_GRACE_MS);
      }, 1000) as unknown as number;
    },
    [router, initiateSSO, openPopup, onError],
  );

  return {
    ssoOptions,
    ssoConfigError,
    handleSSOLogin,
    isInitiatingSSO,
    isLoadingSSOConfig,
    isStandardLoginAllowed: ssoConfig?.allow_standard_login !== false && !ssoConfigError,
  };
};
